import React, { useState, useEffect } from 'react';
import { Button, Loader, Text } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLightbulb,
  faCopy,
  faChevronDown,
  faSearch,
  faTimes,
  faImage,
  faVideo,
  faMicrophone
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { projectService, PromptProject } from '../../services/projectService';
import { useNotification } from '../../context/NotificationContext';
import styles from './PromptBrowser.module.css';

interface PromptBrowserProps {
  onPromptSelect?: (prompt: string, project: PromptProject) => void;
  onPromptCopy?: (prompt: string, project: PromptProject) => void;
  onLoadForImage?: (prompt: string, project: PromptProject) => void;
  onLoadForVideo?: (prompt: string, project: PromptProject) => void;
  onLoadForAudio?: (prompt: string, project: PromptProject) => void;
  onLoadForSound?: (prompt: string, project: PromptProject) => void;
  showActions?: boolean;
  showSelectAction?: boolean;
  showUseActions?: boolean;
  actionLabel?: string;
  height?: string;
  className?: string;
  compact?: boolean;
  projectId?: string; // Add projectId prop to filter prompts by specific project
}

const PromptBrowser: React.FC<PromptBrowserProps> = ({
  onPromptSelect,
  onPromptCopy,
  onLoadForImage,
  onLoadForVideo,
  onLoadForAudio,
  onLoadForSound,
  showActions = true,
  showSelectAction = false,
  showUseActions = true,
  actionLabel = 'Use Prompt',
  height = '400px',
  className = '',
  compact = false,
  projectId // Add projectId to destructuring
}) => {
  const { t } = useTranslation('shared');
  const [promptProjects, setPromptProjects] = useState<PromptProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState<PromptProject | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const { showNotification } = useNotification();

  // Load prompt projects
  useEffect(() => {
    loadPromptProjects();
  }, [projectId]); // Add projectId as dependency

  const loadPromptProjects = async () => {
    try {
      setIsLoading(true);
      
      // If projectId is provided, only load prompts for that specific project
      if (projectId) {
        const prompts = await projectService.getPromptProjectsByProjectId(projectId);
        setPromptProjects(prompts);
      } else {
        // Load all user's prompt projects (existing behavior)
        const prompts = await projectService.getPromptProjects();
        setPromptProjects(prompts);
      }
    } catch (error) {
      console.error('Error loading prompt projects:', error);
      setPromptProjects([]);
      showNotification({
        title: 'Error',
        message: 'Failed to load prompts',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle copying prompt to clipboard
  const handleCopyPrompt = async (prompt: string, project: PromptProject) => {
    try {
      await navigator.clipboard.writeText(prompt);
      showNotification({
        title: 'Copied!',
        message: 'Prompt copied to clipboard',
        type: 'success'
      });
      
      if (onPromptCopy) {
        onPromptCopy(prompt, project);
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      showNotification({
        title: 'Copy failed',
        message: 'Could not copy to clipboard',
        type: 'error'
      });
    }
  };

  // Handle prompt selection
  const handlePromptSelect = (prompt: string, project: PromptProject) => {
    if (onPromptSelect) {
      onPromptSelect(prompt, project);
    }
  };

  // Handle use for image
  const handleLoadForImage = (prompt: string, project: PromptProject) => {
    if (onLoadForImage) {
      onLoadForImage(prompt, project);
      // showNotification({
      //   title: 'Prompt Ready!',
      //   message: 'Prompt ready for image generation',
      //   type: 'success'
      // });
    }
  };

  // Handle use for video
  const handleLoadForVideo = (prompt: string, project: PromptProject) => {
    if (onLoadForVideo) {
      onLoadForVideo(prompt, project);
      // showNotification({
      //   title: 'Prompt Ready!',
      //   message: 'Prompt ready for video generation',
      //   type: 'success'
      // });
    }
  };

  // Handle use for sound/soundtrack
  const handleLoadForSound = (prompt: string, project: PromptProject) => {
    if (onLoadForSound) {
      onLoadForSound(prompt, project);
      // showNotification({
      //   title: 'Prompt Ready!',
      //   message: 'Prompt ready for sound generation',
      //   type: 'success'
      // });
    }
  };

  // Handle use for audio
  const handleLoadForAudio = (prompt: string, project: PromptProject) => {
    if (onLoadForAudio) {
      onLoadForAudio(prompt, project);
      // showNotification({
      //   title: 'Prompt Loaded!',
      //   message: 'Prompt loaded for audio generation',
      //   type: 'success'
      // });
    }
  };

  // Handle dropdown selection
  const handleDropdownSelect = (project: PromptProject) => {
    setSelectedPrompt(project);
    setIsDropdownOpen(false);
  };

  // Get display title - prefer prompt title over project title
  const getDisplayTitle = (project: PromptProject): string => {
    // First, try to use the project title if it's meaningful
    if (project.title && project.title !== 'Untitled Project' && project.title.trim() !== '') {
      return project.title;
    }
    
    // If no meaningful project title, try to generate one from the user's idea
    if (project.promptData?.userSelections?.userIdea) {
      const userIdea = project.promptData.userSelections.userIdea;
      // Truncate long ideas to create a readable title (max 50 characters)
      if (userIdea.length > 50) {
        return userIdea.substring(0, 50).trim() + '...';
      }
      return userIdea;
    }
    
    // Last fallback to project title even if it's generic
    return project.title || 'Untitled Prompt';
  };

  // Filter prompts based on search term
  const filteredPrompts = promptProjects.filter(project => {
    if (!searchTerm) return true;
    const displayTitle = getDisplayTitle(project);
    return displayTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
           project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (project.promptData?.generatedPrompt && 
            project.promptData.generatedPrompt.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (compact) {
    return (
      <div className={`${styles.promptBrowserCompact} ${className}`}>
        {/* Dropdown Selector */}
        <div className={styles.dropdownContainer}>
          <button
            className={styles.dropdownTrigger}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faLightbulb} className={styles.dropdownIcon} />
            <span className={styles.dropdownText}>
              {selectedPrompt ? getDisplayTitle(selectedPrompt) : t('promptBrowser.selectPrompt')}
            </span>
            <FontAwesomeIcon 
              icon={faChevronDown} 
              className={`${styles.dropdownArrow} ${isDropdownOpen ? styles.dropdownArrowOpen : ''}`}
            />
          </button>

          {isDropdownOpen && (
            <div className={styles.dropdownMenu}>
              <div className={styles.dropdownSearch}>
                <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder={t('promptBrowser.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              
              <div className={styles.dropdownOptions}>
                {isLoading ? (
                  <div className={styles.dropdownLoading}>
                    <Loader size="sm" />
                    <span>{t('promptBrowser.loading')}</span>
                  </div>
                ) : filteredPrompts.length === 0 ? (
                  <div className={styles.dropdownEmpty}>
                    <span>{projectId ? t('promptBrowser.noPromptsProject') : t('promptBrowser.noPromptsFound')}</span>
                  </div>
                ) : (
                  filteredPrompts.map((project) => (
                    <button
                      key={project.id}
                      className={styles.dropdownOption}
                      onClick={() => handleDropdownSelect(project)}
                    >
                      <div className={styles.optionContent}>
                        <span className={styles.optionTitle}>{getDisplayTitle(project)}</span>
                        <span className={styles.optionDate}>{formatDate(project.createdAt)}</span>
                      </div>
                      {project.promptData?.generatedPrompt ? (
                        <div className={styles.optionPreview}>
                          {project.promptData.generatedPrompt.slice(0, 80)}...
                        </div>
                      ) : (
                        <div className={styles.optionPreview} style={{ color: '#6b7280', fontStyle: 'italic' }}>
                          {t('promptBrowser.draftMessage')}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Selected Prompt Display */}
        {selectedPrompt && selectedPrompt.promptData && (
          <div className={styles.selectedPromptDisplay}>
            <div className={styles.promptContent}>
              {selectedPrompt.promptData.generatedPrompt ? (
                <div className={styles.promptSection}>
                  <Text size="xs" fw={500} color="dimmed">{t('promptBrowser.generatedPrompt')}</Text>
                  <Text size="sm" className={styles.sectionText}>
                    {selectedPrompt.promptData.generatedPrompt}
                  </Text>
                </div>
              ) : (
                <div className={styles.promptSection}>
                  <Text size="xs" fw={500} color="dimmed">{t('promptBrowser.draftStatus')}</Text>
                  <div style={{
                    padding: '12px',
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <Text size="sm" color="orange" fw={500}>
                      📝 This prompt is still a draft
                    </Text>
                    <Text size="xs" color="dimmed" mt="xs">
                      No generated content available yet. Complete the prompt generation to use it.
                    </Text>
                  </div>
                </div>
              )}
            </div>

            {/* Show action buttons in Selected Prompt Display */}
            {showActions && selectedPrompt.promptData.generatedPrompt && (
              <div className={styles.promptActions}>
                <Button
                  size="xs"
                  onClick={() => handleCopyPrompt(selectedPrompt.promptData!.generatedPrompt, selectedPrompt)}
                  leftSection={<FontAwesomeIcon icon={faCopy} />}
                  className={styles.copyButton}
                >
                  Copy
                </Button>
                {showUseActions && onLoadForImage && (
                  <Button
                    size="xs"
                    onClick={() => handleLoadForImage(selectedPrompt.promptData!.generatedPrompt, selectedPrompt)}
                    leftSection={<FontAwesomeIcon icon={faImage} />}
                    className={styles.useImageButton}
                  >
                    Use for Image
                  </Button>
                )}
                {showUseActions && onLoadForVideo && (
                  <Button
                    size="xs"
                    onClick={() => handleLoadForVideo(selectedPrompt.promptData!.generatedPrompt, selectedPrompt)}
                    leftSection={<FontAwesomeIcon icon={faVideo} />}
                    className={styles.useVideoButton}
                  >
                    Use for Video
                  </Button>
                )}
                {showUseActions && onLoadForAudio && (
                  <Button
                    size="xs"
                    onClick={() => handleLoadForAudio(selectedPrompt.promptData!.generatedPrompt, selectedPrompt)}
                    leftSection={<FontAwesomeIcon icon={faMicrophone} />}
                    className={styles.useAudioButton}
                  >
                    Use for Audio
                  </Button>
                )}
                {showUseActions && onLoadForSound && (
                  <Button
                    size="xs"
                    onClick={() => handleLoadForSound(selectedPrompt.promptData!.generatedPrompt, selectedPrompt)}
                    leftSection={<FontAwesomeIcon icon={faMicrophone} />}
                    className={styles.useAudioButton}
                  >
                    Use for Sound
                  </Button>
                )}
                {showSelectAction && onPromptSelect && (
                  <Button
                    size="xs"
                    onClick={() => handlePromptSelect(selectedPrompt.promptData!.generatedPrompt, selectedPrompt)}
                    className={styles.actionButton}
                  >
                    {actionLabel}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full accordion version (existing code for non-compact mode)
  return (
    <div className={`${styles.promptBrowser} ${className}`}>
      {/* Search Input */}
      <div className={styles.searchContainer}>
        <div className={styles.searchInputWrapper}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            type="text"
            placeholder={t('promptBrowser.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {/* Prompts List */}
      <div className={styles.promptsList} style={{ height }}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <Loader size="md" />
            <Text size="sm" color="dimmed">{t('promptBrowser.loading')}</Text>
          </div>
        ) : filteredPrompts.length === 0 ? (
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faLightbulb} size="2x" className={styles.emptyIcon} />
            <Text size="sm" color="dimmed">
              {searchTerm 
                ? t('promptBrowser.noPromptsFound')
                : projectId 
                  ? t('promptBrowser.noPromptsProject')
                  : t('promptBrowser.noPromptsFound')
              }
            </Text>
            <Text size="xs" color="dimmed">
              {projectId 
                ? t('promptBrowser.createPrompts')
                : t('promptBrowser.createPromptsGeneral')
              }
            </Text>
          </div>
        ) : onPromptSelect ? (
          // Simple list mode when onPromptSelect is provided (for modal usage)
          <div className={styles.simpleList}>
            {filteredPrompts.map((promptProject) => (
              <details key={promptProject.id} className={styles.promptDetails}>
                <summary 
                  className={styles.promptSummary}
                  onClick={(e) => {
                    // If onPromptSelect is provided, prevent default details behavior and trigger modal
                    if (onPromptSelect && promptProject.promptData?.generatedPrompt) {
                      e.preventDefault();
                      handlePromptSelect(promptProject.promptData.generatedPrompt, promptProject);
                    }
                  }}
                >
                  <div className={styles.promptHeader}>
                    <div className={styles.promptTitle}>
                      <FontAwesomeIcon 
                        icon={faLightbulb} 
                        className={styles.promptIcon} 
                        style={{
                          color: promptProject.promptData?.generatedPrompt 
                            ? '#fbbf24' // Yellow/golden for generated (on bulb)
                            : '#6b7280', // Gray for drafts (off bulb)
                          opacity: promptProject.promptData?.generatedPrompt 
                            ? 1.0 // Bright for generated
                            : 0.6 // Dimmed for drafts
                        }}
                      />
                      <span>{getDisplayTitle(promptProject)}</span>
                    </div>
                    <div className={styles.promptDate}>
                      {formatDate(promptProject.createdAt)}
                    </div>
                  </div>
                </summary>
                
                <div className={styles.promptContent}>
                  {/* Generated Prompt or Draft Status */}
                  {promptProject.promptData?.generatedPrompt ? (
                    <div className={styles.promptSection}>
                      <Text size="xs" fw={500} color="dimmed" className={styles.sectionLabel}>
                        {t('promptBrowser.generatedPrompt')}
                      </Text>
                      <Text size="sm" className={styles.sectionContent}>
                        {promptProject.promptData.generatedPrompt}
                      </Text>
                    </div>
                  ) : (
                    <div className={styles.promptSection}>
                      <Text size="xs" fw={500} color="dimmed" className={styles.sectionLabel}>
                        {t('promptBrowser.draftStatus')}
                      </Text>
                      <div style={{
                        padding: '12px',
                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                        border: '1px solid rgba(255, 193, 7, 0.3)',
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <Text size="sm" color="orange" fw={500}>
                          📝 This prompt is still a draft
                        </Text>
                        <Text size="xs" color="dimmed" mt="xs">
                          No generated content available yet. Complete the prompt generation to use it.
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
              </details>
            ))}
          </div>
        ) : (
          // Default list mode when no onPromptSelect provided
          <div className={styles.defaultList}>
            <Text size="sm" color="dimmed">
              Browse and use prompts with the action buttons
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptBrowser;
